import {
  getCrossSellingProducts,
  getFactFinderProducts,
  getFactFinderProductsFallback,
  getProductsByProductReferenceKey,
  getSimilarProducts,
} from 'commerce/factFinder/api';
import { GetFFProductsParams } from 'commerce/factFinder/types';
import { getProductByKey } from 'commerce/scayle/product-management';
import { BapiProduct } from 'commerce/scayle/user-interaction/types';
import { Product } from 'commerce/scayle/utils/adapter';
import { adaptProducts } from './common';

export const getCategoryProductsBackend = async (
  params?: GetFFProductsParams,
) => {
  const { response } = await getFactFinderProducts(params);
  return adaptProducts(response);
};

export const getLongTailProductsBackend = async (
  params?: GetFFProductsParams,
) => {
  const { response } = await getFactFinderProductsFallback(params);

  return adaptProducts(response);
};

export const getSearchProductsBackend = async (
  params?: GetFFProductsParams,
) => {
  let { response } = await getFactFinderProducts(params);

  if (!response?.hits?.length) {
    ({ response } = await getFactFinderProductsFallback(params));
  }

  return adaptProducts(response);
};

export const getRecommendations = async (params: { referenceKey: string }) => {
  const { response: ffResp } = await getSimilarProducts(params.referenceKey);

  return adaptProducts(ffResp);
};

export const getCrossSelling = async (params: { productId: number }) => {
  const { response: ffResp } = await getCrossSellingProducts(params.productId);

  return adaptProducts(ffResp);
};

export const queryProduct = async (
  slug: string,
  getProduct = getProductByKey,
) => {
  const rxReferenceKey = /^.*-(?<referenceKey>[\w]+)$/g;
  const referenceKey = rxReferenceKey
    .exec(slug)
    ?.groups?.referenceKey?.toLowerCase();

  if (!referenceKey) {
    return null;
  }

  let bapiProduct: BapiProduct | undefined;

  /** try to get main product */
  const productResp = await getProduct(referenceKey);

  bapiProduct = productResp.response;

  if (!bapiProduct) {
    /** try to get product variant */
    const variantResp = await getProduct(referenceKey, true);

    bapiProduct = variantResp.response;

    if (
      productResp.error?.status === 404 ||
      variantResp.error?.status === 404 ||
      !bapiProduct
    ) {
      return null;
    }
  }

  const product = Product.createProduct(bapiProduct).toJSON();

  /** get currently displayed variant if selected */
  const activeVariant = product.variants?.find(
    (variant) => variant.referenceKey === referenceKey,
  );

  /** if we are showing a variant (=size) the url should be analog to the product.href */
  const variantUrl = activeVariant?.referenceKey
    ? product.seoUrl.replace(product.referenceKey, activeVariant.referenceKey)
    : '';

  /* For correct seo data */
  const showingVariant = !!activeVariant
    ? Product.createProduct(activeVariant as unknown as BapiProduct).toJSON()
    : product;

  return {
    /** extra level for easier type handling */
    props: {
      product,
      variantUrl,
      referenceKey,
      showingVariant,
    },
  };
};

export const getParentProductByProductReferenceKey = async (
  referenceKey: string,
  getProducts = getProductsByProductReferenceKey,
) => {
  const { response, error } = await getProducts(referenceKey);

  if (error?.status === 404) {
    return;
  }

  /** get parent product */
  return response?.hits?.find(({ masterValues }) =>
    masterValues?.seoUrl?.includes(referenceKey),
  )?.masterValues;
};
